import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import KeyIcon from "@mui/icons-material/Key";
import StoreIcon from "@mui/icons-material/Store";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import Groups3Icon from "@mui/icons-material/Groups3";
import ComputerIcon from "@mui/icons-material/Computer";
import HandymanIcon from "@mui/icons-material/Handyman";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import CalculateIcon from "@mui/icons-material/Calculate";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import TableBarIcon from "@mui/icons-material/TableBar";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import { setShowCashierInfosModal } from "../../store/actions/cashierSlice";
import { LinkStyled } from "../styled";
import {
  canManage,
  isOwner,
} from "../../common/validators";
import { ConfirmationNumber } from "@mui/icons-material";
import Inventory2Icon from "@mui/icons-material/Inventory2";

const MobileMenuContainer = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    maxHeight: "80vh",
    width: "280px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: grey[300],
      borderRadius: 3,
      "&:hover": {
        backgroundColor: grey[400],
      },
    },
  },
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: grey[100],
  },
}));

const DividerWithLabel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  display: "flex",
  justifyContent: "center",
}));

const MobileMenu = ({
  anchorEl,
  open,
  onClose,
  handleRestaurants,
  logout,
  userType,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const user = useSelector((state) => state.auth.user);

  const handleShowCashierInfosModal = () => {
    dispatch(setShowCashierInfosModal(true));
    onClose();
  };

  const handleOpenUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    onClose();
    logout();
  };

  const handleMenuItemClick = () => {
    onClose();
  };

  const handleRestaurantsClick = () => {
    handleRestaurants();
    onClose();
  };

  const renderMainMenuItems = () => {
    return (
      <>
        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/central-delivery" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <ComputerIcon />
              <Typography variant="subtitle1">Central Delivery</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/central-salao" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <TableBarIcon />
              <Typography variant="subtitle1">Central Salão</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/central-embalagem" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <BabyChangingStationIcon />
              <Typography variant="subtitle1">Central Embalagem</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/cozinha" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <SoupKitchenIcon />
              <Typography variant="subtitle1">Cozinha</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <Divider sx={{ my: 1 }} />

        {canManage(userType) && (
          <MenuItemStyled onClick={handleRestaurantsClick}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StoreIcon />
              <Typography variant="subtitle1">Lojas</Typography>
            </Stack>
          </MenuItemStyled>
        )}

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/customers" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <Groups3Icon />
              <Typography variant="subtitle1">Clientes</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/products" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <MenuBookIcon />
              <Typography variant="subtitle1">Cardápios</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>
      </>
    );
  };


  const renderHistoryMenuItems = () => {
    return (
      <>
        <DividerWithLabel>
          <Chip label="Histórico" size="small" />
        </DividerWithLabel>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled
            component={RouterLink}
            to="/historico/pedidos"
            underline="none"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <ListAltIcon />
              <Typography variant="subtitle1">Histórico de Pedidos</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled
            component={RouterLink}
            to="/historico/entregas"
            underline="none"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <DeliveryDiningIcon />
              <Typography variant="subtitle1">Histórico de Entregas</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        {canManage(userType) && (
          <MenuItemStyled onClick={handleMenuItemClick}>
            <LinkStyled
              component={RouterLink}
              to="/historico/caixa"
              underline="none"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <AccountBalanceWalletIcon />
                <Typography variant="subtitle1">Histórico de Caixas</Typography>
              </Stack>
            </LinkStyled>
          </MenuItemStyled>
        )}

        {canManage(userType) && (
          <MenuItemStyled onClick={handleMenuItemClick}>
            <LinkStyled
              component={RouterLink}
              to="/historico/fiscal"
              underline="none"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <ConfirmationNumber />
                <Typography variant="subtitle1">Histórico Notas F.</Typography>
              </Stack>
            </LinkStyled>
          </MenuItemStyled>
        )}
      </>
    );
  };

  const renderAdminMenuItems = () => {
    const hasAdminItems = isOwner(userType);

    if (!hasAdminItems) {
      return null;
    }

    return (
      <>
        <DividerWithLabel>
          <Chip label="Administrador" size="small" />
        </DividerWithLabel>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled component={RouterLink} to="/reports" underline="none">
            <Stack direction="row" spacing={2} alignItems="center">
              <HistoryIcon />
              <Typography variant="subtitle1">Relatórios</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled
            component={RouterLink}
            to="/controle-custos"
            underline="none"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CalculateIcon />
              <Typography variant="subtitle1">Custo Simples</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled
            component={RouterLink}
            to="/assistente-ia"
            underline="none"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <AutoAwesomeIcon />
              <Typography variant="subtitle1">Slapie IA</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled
            component={RouterLink}
            to="/estoque-simples"
            underline="none"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Inventory2Icon />
              <Typography variant="subtitle1">Estoque Simples</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>

        <MenuItemStyled onClick={handleMenuItemClick}>
          <LinkStyled
            component={RouterLink}
            to="/delivery-center"
            underline="none"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <HandymanIcon />
              <Typography variant="subtitle1">Configurações Gerais</Typography>
            </Stack>
          </LinkStyled>
        </MenuItemStyled>
      </>
    );
  };

  const renderBottomMenuItems = () => {
    const isPacking = location.pathname === "/central-embalagem";
    const isSaloon = location.pathname === "/central-salao";
    const shouldShowCashier = !isPacking && !isSaloon;

    return (
      <>
        <Divider sx={{ my: 1 }} />

        {shouldShowCashier && (
          <MenuItemStyled onClick={handleShowCashierInfosModal}>
            <Stack direction="row" spacing={2} alignItems="center">
              <PointOfSaleIcon />
              <Typography variant="subtitle1">Meu Caixa</Typography>
            </Stack>
          </MenuItemStyled>
        )}

        <MenuItemStyled onClick={handleOpenUserMenu}>
          <Stack direction="row" spacing={2} alignItems="center">
            <PersonPinIcon />
            <Typography variant="subtitle1">{user?.username}</Typography>
          </Stack>
        </MenuItemStyled>
      </>
    );
  };

  const renderUserMenu = () => {
    return (
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleCloseUserMenu}>
          <LinkStyled
            component={RouterLink}
            to="/change-password"
            underline="none"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <KeyIcon />
              <Typography>Mudar Senha</Typography>
            </Stack>
          </LinkStyled>
        </MenuItem>
        {isOwner(userType) && (
          <MenuItem onClick={handleCloseUserMenu}>
            <LinkStyled
              component={RouterLink}
              to="/minha-assinatura"
              underline="none"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CardMembershipIcon />
                <Typography>Minha Assinatura</Typography>
              </Stack>
            </LinkStyled>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LogoutIcon />
            <Typography>Sair</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    );
  };

  return (
    <>
      <MobileMenuContainer
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={open}
        onClose={onClose}
      >
        {renderMainMenuItems()}
        {renderHistoryMenuItems()}
        {renderAdminMenuItems()}
        {renderBottomMenuItems()}
      </MobileMenuContainer>
      {renderUserMenu()}
    </>
  );
};

export default MobileMenu;
